import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FacetService from '../../../../patterns/organisms/Product/ProductListing/FacetService';

import { useSessionStorage } from 'react-use';
import { usePathname } from 'next/navigation';

const DEFAULT_STEP = 32;

export const CategoryContext = createContext({});

export const CategoryProvider = ({ children, data, objects }) => {
  const { context, urlHandler, tastic, isSSR, node: currentPage, route } = objects;
  const pathname = usePathname();
  const currency = context?.currency;
  const phrase = decodeURIComponent(route.parameters?.phrase || '')?.replace(/\+/g, ' ');

  const [triggerViewAll, setTriggerViewAll] = useState(false);
  const [stream, setStream] = useState(data?.stream);
  const [stableItems, setStableItems] = useState([]);
  const [isGlobalLoaderShow, setIsGlobalLoaderShow] = useState(false);
  const [pushedItems, setPushedItems] = useState(0);
  const [mergedFacets, setMergedFacets] = useState([]);
  const [initialPage, setInitialPage] = useState(1);
  const [infiniteScroll, setInfiniteScroll] = useState(false);
  const [routeParams, setRouteParams] = useState({});
  const [itemOffset, setItemOffset] = useState(stream.offset);
  const [triggerFilter, setTriggerFilter] = useState(false);
  const [localFilters, setLocalFilters] = useState({});

  const [fetchProductsCache, setFetchProductsCache] = useState({
    [stream?.offset]: data?.stream,
  });
  const [viewMode, setViewMode] = useSessionStorage('prefferedViewMode', 'wide');
  const [searchPhrase, setSearchPhrase] = useState(phrase ?? '');

  const isSearchPage = currentPage.nodeType === 'search';
  const isCuratedForYou = currentPage.nodeType === 'apg/curated-for-you';
  const isWideViewMode = viewMode === 'wide';
  const layoutCount = isWideViewMode ? 4 : 2;

  const productListRef = useRef(null);
  const allPagesLoaded = useRef(false);

  const triggerRoute = route.parameters.url;

  const categoryList = stream?.items?.map((x, index) => ({
    ...x,
    position: index + 1,
  }));

  const streamId =
    currentPage.streams.find((s) => s.type === 'apg/product-list' && s.streamId === '__master')?.streamId ??
    currentPage.streams.find((s) => s.type === 'apg/product-list')?.streamId;

  const hasPromoTile = data?.promoTile?.length > 0;
  const parameters = urlHandler && urlHandler.parameterReader(tastic.configuration.stream).getParameters();

  let sortState = {};
  const sortAttributes = Object.entries(stream?.query?.sortAttributes ?? {});
  if (sortAttributes.length > 0) {
    sortState = {
      attributeId: sortAttributes[0][0],
      order: sortAttributes[0][1],
    };
  }

  const getSelectedFacets = () => {
    return stream?.facets && sortState?.attributeId
      ? FacetService.numberOfSelectedFacets(stream.facets) + 1
      : stream.facets
        ? FacetService.numberOfSelectedFacets(stream.facets)
        : sortState?.attributeId
          ? 1
          : '';
  };

  const [selectedFacets, setSelectedFacets] = useState(getSelectedFacets());

  const getSelectedFilters = (facets) => {
    return facets && sortState?.attributeId
      ? [
          ...FacetService.getSelectedFacets(facets),
          {
            attributeId: sortState.sortAttributeId,
            order: sortState.sortOrder,
            type: 'sort',
          },
        ]
      : facets
        ? FacetService.getSelectedFacets(facets)
        : [];
  };

  const [selectedFilters, setSelectedFilters] = useState(getSelectedFilters(stream?.facets));

  const itemsLimit = context?.project?.configuration?.category?.limit ?? DEFAULT_STEP;
  const [pageCount, setPageCount] = useState(Math.ceil(stream.total / itemsLimit));

  const filterCount = useMemo(
    () =>
      mergedFacets.reduce((acc, filter) => {
        if (filter.type === 'term' || filter.type === 'boolean') {
          return acc + filter.terms?.filter((term) => term.selected).length;
        }

        if (filter.type === 'range') {
          return acc + (filter.selected ? 1 : 0);
        }

        return acc;
      }, 0),
    [mergedFacets],
  );

  useEffect(() => {
    const newOffset = stream.offset;
    setItemOffset(newOffset);
    const endOffset = newOffset + itemsLimit;
    setPageCount(Math.ceil(stream.total / itemsLimit));
    const initPage = Math.ceil(endOffset / itemsLimit);
    setInitialPage(initPage || 1);
  }, [stream.total, stream.offset, itemOffset, itemsLimit]);

  useEffect(() => {
    setStream(data?.stream);
    if (!stableItems?.length) {
      setStableItems(stream?.items);
      return;
    }

    if (data?.stream?.items?.length !== !stableItems?.length) {
      setStableItems(data?.stream?.items);
    }
  }, [triggerRoute, data]);

  useEffect(() => {
    if (!isSearchPage) return;
    if (!pathname) return;

    const slug = decodeURIComponent(pathname.split('/').pop() || '')?.replace(/\+/g, ' ');
    if (slug !== searchPhrase) {
      setSearchPhrase(slug);
    }
  }, [isSearchPage, triggerRoute, pathname]);

  const getListName = useCallback(() => {
    if (!isSSR) {
      return window.location.pathname.indexOf('/q/') === 0 ? 'Search' : currentPage.name;
    }
  }, [isSSR, data]);

  const filterTagsData = useMemo(() => {
    const termFacets = mergedFacets
      ?.filter((facet) => facet.type === 'term')
      ?.flatMap((facet) => {
        const attrId = facet.identifier;

        const localTerms = localFilters?.[attrId]?.terms || [];
        const facetTerms = facet.terms || [];

        const mergedTerms = [...new Map([...localTerms, ...facetTerms].map((t) => [t.value, t])).values()];

        return mergedTerms
          .map((term) => ({
            ...term,
            selected:
              localTerms.some((f) => f.value === term.value && f.selected) &&
              facetTerms.some((f) => f.value === term.value && f.selected),
          }))
          .filter((term) => term.selected)
          .map((term) => ({
            name: term.name,
            value: term.value,
            attrId: facet.identifier,
          }));
      });

    const priceRangeInFacets = mergedFacets?.find((facet) => facet.type === 'range');
    const priceRangeInLocalFilters = localFilters?.['variants.price'];

    if (priceRangeInFacets?.selected && Boolean(priceRangeInLocalFilters)) {
      termFacets.push({
        attrId: priceRangeInFacets.attributeId,
        name: `$${Math.ceil(Number(priceRangeInFacets.minSelected) / 100)} - $${Math.ceil(Number(priceRangeInFacets.maxSelected) / 100)}`,
      });
    }

    return termFacets;
  }, [mergedFacets, localFilters]);

  const breadcrumbs = [
    { id: 'bc-home', name: 'Home', reference: { target: '/', type: 'link' } },
    ...(stream?.projectSpecificData?.categories?.map((item) => ({
      ...item,
      reference: { target: item.url || '', type: 'link' },
    })) || []),
  ];

  const valueObj = useMemo(() => {
    return {
      data,
      isSSR,
      triggerViewAll,
      stableItems,
      isGlobalLoaderShow,
      stream,
      pushedItems,
      mergedFacets,
      initialPage,
      infiniteScroll,
      routeParams,
      itemOffset,
      triggerFilter,
      pageCount,
      selectedFacets,
      selectedFilters,
      currentPage,
      searchPhrase,
      productListRef,
      allPagesLoaded,
      triggerRoute,
      viewMode,
      isSearchPage,
      layoutCount,
      fetchProductsCache,
      filterCount,
      currency,
      sortState,
      categoryList,
      streamId,
      parameters,
      hasPromoTile,
      itemsLimit,
      localFilters,
      setLocalFilters,
      ...objects,
      setTriggerViewAll,
      filterTagsData,
      setViewMode,
      setStableItems,
      setStream,
      setIsGlobalLoaderShow,
      setPushedItems,
      setMergedFacets,
      setInitialPage,
      setInfiniteScroll,
      setRouteParams,
      setItemOffset,
      setTriggerFilter,
      setPageCount,
      setSelectedFacets,
      setSelectedFilters,
      setFetchProductsCache,
      getSelectedFilters,
      getSelectedFacets,
      getListName,
      breadcrumbs,
      isCuratedForYou,
    };
  }, [
    isSSR,
    data,
    objects,
    stream,
    currency,
    triggerViewAll,
    stableItems,
    isGlobalLoaderShow,
    pushedItems,
    mergedFacets,
    initialPage,
    infiniteScroll,
    routeParams,
    itemOffset,
    triggerFilter,
    pageCount,
    selectedFacets,
    selectedFilters,
    searchPhrase,
    productListRef,
    allPagesLoaded,
    triggerRoute,
    sortState,
    viewMode,
    fetchProductsCache,
    filterCount,
    breadcrumbs,
    isCuratedForYou,
  ]);

  return <CategoryContext.Provider value={valueObj}>{children}</CategoryContext.Provider>;
};
